const DB_NAME = 'ThinkMDDB'
const DB_VERSION = 3

const STORES = {
  PATIENT_VISIT_INSIGHTS: 'patientVisitInsights',
}

/** @type {Promise<IDBDatabase> | null} */
let _dbPromise = null

/**
 * Open (or return the cached) IndexedDB database connection.
 * The promise is reused across all calls so the DB is only opened once.
 * @returns {Promise<IDBDatabase>}
 */
function openDB() {
  if (!_dbPromise) {
    _dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION)

      request.onupgradeneeded = (event) => {
        const db = event.target.result
        if (!db.objectStoreNames.contains(STORES.PATIENT_VISIT_INSIGHTS)) {
          const store = db.createObjectStore(STORES.PATIENT_VISIT_INSIGHTS, {
            keyPath: 'id',
            autoIncrement: true,
          })
          store.createIndex('createdAt', 'createdAt', { unique: false })
          store.createIndex('patientType', 'patientType', { unique: false })
          store.createIndex('encounterId', 'encounterId', { unique: false })
          store.createIndex('userId', 'userId', { unique: false })
          store.createIndex('deviceId', 'deviceId', { unique: false })
          store.createIndex('deviceId_userId', ['deviceId', 'userId'], { unique: false })
        }
      }

      request.onsuccess = (event) => {
        const db = event.target.result
        db.onclose = () => {
          // Browser killed the connection (e.g. storage eviction) — reset so
          // the next call re-opens cleanly.
          _dbPromise = null
        }
        resolve(db)
      }

      request.onerror = (event) => {
        _dbPromise = null
        reject(event.target.error)
      }

      request.onblocked = () => {
        console.warn('[indexedDBUtils] DB upgrade blocked by an older tab.')
      }
    }).catch((err) => {
      // Reset on error so the next call retries instead of returning a
      // permanently rejected promise.
      _dbPromise = null
      throw err
    })
  }
  return _dbPromise
}

/**
 * Wrap an IDBRequest in a Promise.
 * @param {IDBRequest} request
 * @returns {Promise<any>}
 */
function promisifyRequest(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = (event) => resolve(event.target.result)
    request.onerror = (event) => reject(event.target.error)
  })
}

/**
 * Add a single record to an object store.
 * @param {string} storeName
 * @param {object} record
 * @returns {Promise<IDBValidKey>} - the generated key
 */
async function addRecord(storeName, record) {
  const db = await openDB()
  const tx = db.transaction(storeName, 'readwrite')
  const key = await promisifyRequest(tx.objectStore(storeName).add(record))
  await new Promise((resolve, reject) => {
    tx.oncomplete = resolve
    tx.onerror = (e) => reject(e.target.error)
  })
  return key
}

/**
 * Add multiple records to an object store in a single transaction.
 * @param {string} storeName
 * @param {object[]} records
 * @returns {Promise<IDBValidKey[]>} - array of generated keys
 */
async function addRecords(storeName, records) {
  if (!records || records.length === 0) return []

  const db = await openDB()
  const tx = db.transaction(storeName, 'readwrite')
  const store = tx.objectStore(storeName)
  const keys = await Promise.all(records.map((record) => promisifyRequest(store.add(record))))
  await new Promise((resolve, reject) => {
    tx.oncomplete = resolve
    tx.onerror = (e) => reject(e.target.error)
  })
  return keys
}

/**
 * Retrieve all records from an object store.
 * @param {string} storeName
 * @returns {Promise<object[]>}
 */
async function getAllRecords(storeName) {
  const db = await openDB()
  const tx = db.transaction(storeName, 'readonly')
  return promisifyRequest(tx.objectStore(storeName).getAll())
}

/**
 * Retrieve a single record by its key.
 * @param {string} storeName
 * @param {IDBValidKey} key
 * @returns {Promise<object|undefined>}
 */
async function getRecordByKey(storeName, key) {
  const db = await openDB()
  const tx = db.transaction(storeName, 'readonly')
  return promisifyRequest(tx.objectStore(storeName).get(key))
}

/**
 * Update an existing record (replaces the entire record).
 * @param {string} storeName
 * @param {object} record - must include the keyPath field (id)
 * @returns {Promise<IDBValidKey>}
 */
async function updateRecord(storeName, record) {
  const db = await openDB()
  const tx = db.transaction(storeName, 'readwrite')
  const key = await promisifyRequest(tx.objectStore(storeName).put(record))
  await new Promise((resolve, reject) => {
    tx.oncomplete = resolve
    tx.onerror = (e) => reject(e.target.error)
  })
  return key
}

/**
 * Retrieve the first record matching an index value.
 * @param {string} storeName
 * @param {string} indexName
 * @param {IDBValidKey | IDBKeyRange} query
 * @returns {Promise<object|undefined>}
 */
async function getRecordByIndex(storeName, indexName, query) {
  if (query === undefined || query === null) return undefined

  const db = await openDB()
  const tx = db.transaction(storeName, 'readonly')
  return promisifyRequest(tx.objectStore(storeName).index(indexName).get(query))
}

/**
 * Retrieve all records matching an index value.
 * @param {string} storeName
 * @param {string} indexName
 * @param {IDBValidKey | IDBKeyRange} query
 * @returns {Promise<object[]>}
 */
async function getAllRecordsByIndex(storeName, indexName, query) {
  if (query === undefined || query === null) return []

  const db = await openDB()
  const tx = db.transaction(storeName, 'readonly')
  return promisifyRequest(tx.objectStore(storeName).index(indexName).getAll(query))
}

/**
 * Delete a record by its key.
 * @param {string} storeName
 * @param {IDBValidKey} key
 * @returns {Promise<void>}
 */
async function deleteRecord(storeName, key) {
  const db = await openDB()
  const tx = db.transaction(storeName, 'readwrite')
  await promisifyRequest(tx.objectStore(storeName).delete(key))
  await new Promise((resolve, reject) => {
    tx.oncomplete = resolve
    tx.onerror = (e) => reject(e.target.error)
  })
}

/**
 * Clear all records from an object store.
 * @param {string} storeName
 * @returns {Promise<void>}
 */
async function clearStore(storeName) {
  const db = await openDB()
  const tx = db.transaction(storeName, 'readwrite')
  await promisifyRequest(tx.objectStore(storeName).clear())
  await new Promise((resolve, reject) => {
    tx.oncomplete = resolve
    tx.onerror = (e) => reject(e.target.error)
  })
}

module.exports = {
  addRecord,
  addRecords,
  getAllRecords,
  getRecordByKey,
  updateRecord,
  getRecordByIndex,
  getAllRecordsByIndex,
  deleteRecord,
  clearStore,
  STORES,
}
